import { useState, useEffect, useRef } from 'react';
import { db } from '../services/firebase';
import { doc, getDoc, setDoc, deleteField, collection, query, where, getDocs } from 'firebase/firestore';
import { useProtectedAction } from '../hooks/useProtectedAction';
import { useAuth } from '../context/AuthContext';

// Conexão de aparelho passa pelo webhook do n8n (a chave da Evolution NÃO fica no navegador).
const WEBHOOK = 'https://fatima.agenciafalei.com.br/webhook/conectar-numero';

// Limite de aparelhos incluso no plano. Aparelho adicional = R$29,90/mês.
// Liberação por conta: campo `limiteAparelhos` (número) no documento
// users/{uid}/config/aparelhos — editado só pelo console do Firebase / n8n.
//
// Quem usa cada aparelho: campo `donos` do mesmo documento, { nome: uid }.
// Aparelho sem dono é da equipe toda. Quem não é Administrador vê só os
// da equipe e os próprios — a não ser que o Administrador tenha ligado
// "vê os números de toda a equipe" para ela (vinculos/{uid}.verTodosNumeros).
// O n8n (FaLei Portal · WhatsApp API) aplica a mesma regra nas conversas.
const LIMITE_PADRAO = 1;
const PRECO_EXTRA = 'R$29,90/mês';
const WHATS_SUPORTE = '5511966192846'; // João — CONFIRME se é este o número

// Nome de instância na Evolution não aceita acento, espaço nem símbolo.
// Antes isso virava erro no create e a pessoa tentava outro nome, deixando lixo.
const normalizarNome = (v) => String(v || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 40);

// O QR pode vir em formatos diferentes dependendo da versão da Evolution.
const extrairBase64 = (d) => {
  const b = d?.base64 || d?.qrcode?.base64 || d?.qr?.base64 || d?.qrcode || d?.qr || null;
  if (!b || typeof b !== 'string') return null;
  return b.startsWith('data:') ? b : `data:image/png;base64,${b}`;
};

export default function Configuracoes() {
  const { protect, Modals, temPlano } = useProtectedAction(true);
  const { user, contaUid, papel, vinculo } = useAuth();
  // Vendas conecta e reconecta; remover (apaga a instância e os disparos
  // agendados dela) fica só com o Administrador.
  const ehAdmin = papel === 'admin';
  const podeRemover = ehAdmin;
  const veTodos = ehAdmin || !!vinculo?.verTodosNumeros;
  const [equipe, setEquipe] = useState([]);           // só o Administrador usa (escolher quem usa)
  const [erroDono, setErroDono] = useState('');
  const [aparelhos, setAparelhos] = useState([]);
  const [nomeNovo, setNomeNovo]   = useState('');
  const [qr, setQr]               = useState(null);   // { nome, base64, modo }
  const [ocupado, setOcupado]     = useState('');     // nome do aparelho em operação
  const [statusQR, setStatusQR]   = useState('');
  const [limite, setLimite]       = useState(LIMITE_PADRAO);
  const [checando, setChecando]   = useState(false);

  const pollRef = useRef(null);
  const limparPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => limparPoll(), []);

  // uid vem do login real do Firebase (não do localStorage)
  useEffect(() => {
    if (user && contaUid) carregarAparelhos(contaUid);
    else setAparelhos([]);
    // eslint-disable-next-line
  }, [user, contaUid]);

  // referência do documento da lista de aparelhos do franqueado
  const refAparelhos = (u) => doc(db, 'users', u, 'config', 'aparelhos');

  // consulta o estado de uma instância via webhook (action: status)
  const consultarStatus = async (instancia) => {
    try {
      const r = await fetch(WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'status', instancia })
      });
      const d = await r.json();
      return !!d.connected;
    } catch (e) {
      return false;
    }
  };

  // lê a lista do Firestore (SDK autenticado) e confere o status de cada um
  const carregarAparelhos = async (u) => {
    const userUid = u || contaUid;
    if (!userUid) return;
    setChecando(true);
    try {
      const snap = await getDoc(refAparelhos(userUid));
      const dados = snap.exists() ? snap.data() : {};
      // dedupe: versões antigas chegaram a gravar o mesmo nome duas vezes
      const lista = [...new Set((dados.lista || []).filter(Boolean))];
      setLimite(parseInt(dados.limiteAparelhos) || LIMITE_PADRAO);
      const donos = dados.donos || {};
      const comStatus = await Promise.all(lista.map(async (nome) => {
        const conectado = await consultarStatus(nome);
        return { nome, conectado, dono: donos[nome] || null };
      }));
      setAparelhos(comStatus);
    } catch (e) {
      setAparelhos([]);
    } finally { setChecando(false); }
  };

  // salva a lista no Firestore (SDK autenticado)
  // `extra`: outros campos do documento (ex.: donos), gravados junto.
  const salvarLista = async (lista, extra = {}) => {
    const u = contaUid;
    if (!u) return;
    // merge: true preserva o campo limiteAparelhos (liberado manualmente)
    await setDoc(refAparelhos(u), { lista: [...new Set(lista)], atualizadoEm: new Date().toISOString(), ...extra }, { merge: true });
  };

  // Lista da equipe, para o Administrador escolher quem usa cada aparelho.
  useEffect(() => {
    if (!ehAdmin || !contaUid) return;
    getDocs(query(collection(db, 'vinculos'), where('donoUid', '==', contaUid)))
      .then((s) => setEquipe(s.docs.map((d) => ({ uid: d.id, ...d.data() })).filter((m) => m.ativo !== false)))
      .catch((e) => setErroDono('Não consegui ler a equipe: ' + (e.code || e.message)));
  }, [ehAdmin, contaUid]);

  const nomeDoDono = (uid) => {
    if (!uid) return 'Equipe toda';
    if (uid === user?.uid) return 'Você';
    const m = equipe.find((x) => x.uid === uid);
    return m ? (m.nome || m.email) : 'Outra pessoa';
  };

  // Administrador troca quem usa o aparelho ('' = equipe toda).
  const trocarDono = async (nome, uid) => {
    setErroDono('');
    try {
      await setDoc(refAparelhos(contaUid), { donos: { [nome]: uid || deleteField() } }, { merge: true });
      carregarAparelhos();
    } catch (e) {
      setErroDono('Não consegui trocar quem usa "' + nome + '": ' + (e.code || e.message));
    }
  };

  // ── Pedir o QR ──────────────────────────────────────────────────────────────
  // A ordem das ações importa. Para um aparelho que JÁ EXISTE tentamos 'connect'
  // primeiro: é ele que devolve o QR da mesma instância na Evolution. Só se o
  // n8n não tiver essa ação é que cai no 'create' — e ainda assim com o MESMO
  // nome, que é o que impede a Evolution de encher de instância órfã.
  const pedirQR = async (nome, acoes) => {
    let ultimoErro = '';
    for (const action of acoes) {
      try {
        const r = await fetch(WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, instancia: nome })
        });
        const d = await r.json().catch(() => ({}));
        if (d.connected) return { jaConectado: true, instancia: d.instancia || nome };
        const base64 = extrairBase64(d);
        if (base64) return { base64, instancia: d.instancia || nome };
        ultimoErro = d.erro || d.error || d.message || '';
      } catch (e) {
        ultimoErro = e.message;
      }
    }
    return { erro: ultimoErro || 'não consegui gerar o QR' };
  };

  // Relê a lista do Firestore antes de gravar: o estado da tela pode estar
  // defasado depois de 2 minutos de QR aberto, e gravar por cima apagaria
  // um aparelho conectado em outra aba.
  const adicionarNaLista = async (nome) => {
    const u = contaUid;
    if (!u) return;
    let atual = [];
    try {
      const snap = await getDoc(refAparelhos(u));
      atual = (snap.exists() ? snap.data().lista : []) || [];
    } catch (e) { atual = aparelhos.map(a => a.nome); }
    // Aparelho NOVO conectado por quem não é Administrador fica sendo dela.
    // Reconectar um que já existe não muda o dono.
    const novo = !atual.includes(nome);
    await salvarLista([...atual, nome], novo && !ehAdmin && user?.uid ? { donos: { [nome]: user.uid } } : {});
  };

  // Fica checando até conectar. Ao conectar, garante o nome na lista (sem duplicar).
  const acompanharConexao = (nome) => {
    limparPoll();
    pollRef.current = setInterval(async () => {
      const conectado = await consultarStatus(nome);
      if (!conectado) return;
      limparPoll();
      try {
        await adicionarNaLista(nome);
      } catch (e) {
        // Sem isto o QR ficava aberto e ninguém sabia que a lista não foi salva.
        setQr(null); setOcupado('');
        setStatusQR('erro:O WhatsApp conectou, mas não consegui salvar o aparelho na lista da conta (' + (e.code || e.message) + '). Avise o administrador.');
        return;
      }
      setQr(null);
      setStatusQR('conectado');
      setNomeNovo('');
      setOcupado('');
      carregarAparelhos();
    }, 3000);
    // 2 minutos é a validade do QR da Evolution
    setTimeout(() => {
      if (!pollRef.current) return;
      limparPoll();
      setOcupado('');
      setQr(q => (q && q.nome === nome ? null : q));
      setStatusQR('erro:O QR expirou. Clique em Reconectar para gerar outro.');
    }, 120000);
  };

  // ── Reconectar um aparelho que já existe ────────────────────────────────────
  // É este botão que faltava. Antes, aparelho caído só tinha "Remover", e aí a
  // pessoa conectava com OUTRO nome — criando instância nova na Evolution e
  // deixando todo disparo agendado apontando pra um aparelho que não existe mais.
  const reconectar = async (nome) => {
    setOcupado(nome); setQr(null); setStatusQR('');
    const r = await pedirQR(nome, ['connect', 'create']);
    if (r.jaConectado) {
      setStatusQR('conectado'); setOcupado('');
      carregarAparelhos();
      return;
    }
    if (r.base64) {
      setQr({ nome, base64: r.base64, modo: 'reconectar' });
      setStatusQR('escaneando');
      acompanharConexao(nome);
      return;
    }
    setOcupado('');
    setStatusQR('erro:' + r.erro);
  };

  const conectarAparelho = async () => {
    const nome = normalizarNome(nomeNovo);
    if (!nome) { setStatusQR('erro:Dê um nome ao aparelho (só letras e números).'); return; }

    // Mesmo nome de um aparelho já cadastrado = reconectar, nunca criar outro.
    if (aparelhos.some(a => a.nome === nome)) {
      if (!visiveis.some(a => a.nome === nome)) { setStatusQR('erro:Esse nome já é de outro aparelho da conta. Escolha outro nome.'); return; }
      reconectar(nome); return;
    }

    setOcupado(nome); setQr(null); setStatusQR('');
    const r = await pedirQR(nome, ['create', 'connect']);
    if (r.jaConectado) {
      try {
        await adicionarNaLista(r.instancia || nome);
      } catch (e) {
        setOcupado('');
        setStatusQR('erro:O WhatsApp conectou, mas não consegui salvar o aparelho na lista da conta (' + (e.code || e.message) + '). Avise o administrador.');
        return;
      }
      setStatusQR('conectado'); setOcupado(''); setNomeNovo('');
      carregarAparelhos();
      return;
    }
    if (r.base64) {
      const criada = r.instancia || nome;
      setQr({ nome: criada, base64: r.base64, modo: 'novo' });
      setStatusQR('escaneando');
      acompanharConexao(criada);
      return;
    }
    setOcupado('');
    setStatusQR('erro:' + r.erro);
  };

  // solicita aparelho adicional via WhatsApp do suporte (liberação manual)
  const solicitarAparelhoExtra = () => {
    const msg = encodeURIComponent(
      'Olá! Quero adicionar um aparelho adicional (' + PRECO_EXTRA + ') à minha conta do painel. Meu ID: ' + (contaUid || '—')
    );
    window.open('https://wa.me/' + WHATS_SUPORTE + '?text=' + msg, '_blank');
  };

  const remover = async (nome) => {
    if (!window.confirm(
      `Remover "${nome}"?\n\nSe você só quer religar o WhatsApp, use RECONECTAR — remover apaga a instância e todo disparo agendado neste aparelho para de sair.`
    )) return;
    setOcupado(nome);
    try {
      await fetch(WEBHOOK, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', instancia: nome })
      });
      const novaLista = aparelhos.map(a => a.nome).filter(n => n !== nome);
      await salvarLista(novaLista, { donos: { [nome]: deleteField() } });
      carregarAparelhos();
    } catch (e) {} finally { setOcupado(''); }
  };

  const nomePreview = normalizarNome(nomeNovo);
  const nomeJaExiste = !!nomePreview && aparelhos.some(a => a.nome === nomePreview);
  // O que esta pessoa enxerga. O limite do plano conta todos da conta.
  const visiveis = aparelhos.filter(a => veTodos || !a.dono || a.dono === user?.uid);
  const desconectados = visiveis.filter(a => !a.conectado).length;

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto' }}>

      {Modals}

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: '26px',
          fontWeight: '800', letterSpacing: '-0.5px', marginBottom: '6px' }}>
          {'Configurações'}
        </h1>
        <p style={{ color: '#52526e', fontSize: '13px' }}>
          Gerencie seus aparelhos de envio conectados.
        </p>
      </div>

      {/* Aparelhos */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700', fontSize: '15px' }}>
            {'Aparelhos de envio'}
            <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: '600', color: '#6c47ff', marginLeft: '10px' }}>{aparelhos.length}/{limite}</span>
          </div>
          <button onClick={() => carregarAparelhos()} disabled={checando}
            style={{ border: '1.5px solid rgba(0,0,0,0.1)', background: 'none', borderRadius: '8px',
              padding: '6px 12px', fontSize: '12px', color: '#52526e',
              cursor: checando ? 'wait' : 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            {checando ? '⟳ Checando...' : '⟳ Atualizar status'}
          </button>
        </div>

        {desconectados > 0 && (
          <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
            borderRadius: '10px', padding: '12px 14px', marginBottom: '14px',
            fontSize: '12.5px', color: '#92400e', lineHeight: 1.6 }}>
            ⚠ {desconectados === 1 ? 'Um aparelho está desconectado' : `${desconectados} aparelhos estão desconectados`}.
            Use <b>Reconectar</b> para ligar o <b>mesmo</b> número de volta — assim os disparos já agendados
            continuam de onde pararam. Conectar com outro nome cria um aparelho novo e deixa os agendamentos parados.
          </div>
        )}

        {erroDono && (
          <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '12.5px', color: '#b91c1c' }}>
            {erroDono}
          </div>
        )}

        {visiveis.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px',
            color: '#9898b8', fontSize: '13px' }}>
            Nenhum aparelho conectado ainda.
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            {visiveis.map((ap, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', borderRadius: '10px', marginBottom: '8px', gap: '10px',
                background: '#f8f8fc',
                border: '1px solid ' + (ap.conectado ? 'rgba(0,0,0,0.07)' : 'rgba(239,68,68,0.25)')
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <div style={{
                    width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                    background: ap.conectado ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '18px'
                  }}>{'📱'}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: '600', fontSize: '13px', overflow: 'hidden',
                      textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ap.nome}</div>
                    <div style={{ fontSize: '11px', fontFamily: 'monospace', marginTop: '2px',
                      color: ap.conectado ? '#059669' : '#dc2626' }}>
                      {ap.conectado ? '● Conectado' : '● Desconectado'}
                      {!ehAdmin && <span style={{ color: '#9898b8' }}> · {nomeDoDono(ap.dono)}</span>}
                    </div>
                    {ehAdmin && (
                      <select value={ap.dono || ''} onChange={e => trocarDono(ap.nome, e.target.value)}
                        aria-label={'Quem usa ' + ap.nome}
                        style={{ marginTop: '6px', fontSize: '11.5px', padding: '3px 6px', borderRadius: '6px',
                          border: '1px solid rgba(0,0,0,0.12)', background: '#fff', maxWidth: '220px' }}>
                        <option value="">Quem usa: equipe toda</option>
                        {user?.uid && <option value={user.uid}>Quem usa: você (só o Administrador vê)</option>}
                        {ap.dono && ap.dono !== user?.uid && !equipe.some(m => m.uid === ap.dono) && (
                          <option value={ap.dono}>Quem usa: pessoa fora da equipe</option>
                        )}
                        {equipe.map(m => <option key={m.uid} value={m.uid}>Quem usa: {m.nome || m.email}</option>)}
                      </select>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  {!ap.conectado && (
                    <button onClick={protect(() => reconectar(ap.nome))} disabled={!!ocupado}
                      title="Gera um QR novo para o MESMO aparelho — os disparos agendados continuam valendo"
                      style={{
                        background: ocupado === ap.nome ? '#9898b8' : 'linear-gradient(135deg, #6c47ff, #5535e0)',
                        color: '#fff', border: 'none', borderRadius: '8px', padding: '7px 14px',
                        fontSize: '12px', fontWeight: '600',
                        cursor: ocupado ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
                      {ocupado === ap.nome ? '⟳ Gerando...' : '🔄 Reconectar'}
                    </button>
                  )}
                  {podeRemover && (
                    <button onClick={() => remover(ap.nome)} disabled={!!ocupado}
                      style={{ border: '1px solid rgba(239,68,68,0.3)', background: 'none',
                        borderRadius: '8px', padding: '6px 14px', fontSize: '12px',
                        cursor: ocupado ? 'not-allowed' : 'pointer', color: '#dc2626' }}>
                      Remover
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Conectar novo / Upsell aparelho extra */}
        <div style={{ borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '16px' }}>
          {aparelhos.length >= limite ? (
            <div style={{ background: 'rgba(108,71,255,0.05)', border: '1px solid rgba(108,71,255,0.2)',
              borderRadius: '12px', padding: '20px' }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '800', fontSize: '15px', marginBottom: '8px' }}>
                ✨ Adicionar outro aparelho
              </div>
              <p style={{ fontSize: '13px', color: '#52526e', lineHeight: 1.6, marginBottom: '16px' }}>
                Seu plano inclui <b>{limite} aparelho{limite > 1 ? 's' : ''}</b>. Para conectar mais um número de WhatsApp e disparar
                por mais aparelhos, há um adicional de <b>{PRECO_EXTRA}</b> por aparelho.
                {desconectados > 0 && (
                  <><br /><b style={{ color: '#92400e' }}>Se o seu aparelho só caiu, não precisa disso:</b> use o botão <b>Reconectar</b> acima.</>
                )}
              </p>
              <button onClick={solicitarAparelhoExtra} style={{
                background: 'linear-gradient(135deg, #6c47ff, #5535e0)', color: '#fff', border: 'none',
                borderRadius: '10px', padding: '11px 22px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                Quero adicionar um aparelho →
              </button>
              <div style={{ fontSize: '11px', color: '#9898b8', marginTop: '10px' }}>
                Você fala com nosso time, que envia o link e libera o aparelho extra.
              </div>
            </div>
          ) : (
          <>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#52526e',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            fontFamily: 'monospace', marginBottom: '10px' }}>
            Conectar novo aparelho
          </div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
            <input value={nomeNovo} onChange={e => setNomeNovo(e.target.value)}
              placeholder="Ex: meu_celular_01"
              style={{ ...input, margin: 0 }}
              onKeyDown={e => e.key === 'Enter' && protect(conectarAparelho)()} />
            <button onClick={protect(conectarAparelho)} disabled={!!ocupado || !nomePreview}
              style={{
                background: ocupado || !nomePreview
                  ? '#9898b8' : 'linear-gradient(135deg, #6c47ff, #5535e0)',
                color: '#fff', border: 'none', borderRadius: '10px',
                padding: '10px 20px', fontSize: '13px', fontWeight: '600',
                cursor: ocupado || !nomePreview ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap'
              }}>
              {ocupado ? 'Gerando...' : nomeJaExiste ? 'Reconectar' : 'Gerar QR'}
            </button>
          </div>
          <div style={{ fontSize: '11px', color: '#9898b8', marginBottom: '14px' }}>
            {nomeJaExiste
              ? <span style={{ color: '#b45309' }}>Esse aparelho já existe — vou reconectar o mesmo, sem criar outro.</span>
              : nomePreview && nomePreview !== nomeNovo.trim()
                ? <>Vai ser salvo como <b style={{ fontFamily: 'monospace' }}>{nomePreview}</b> (sem acento, espaço ou símbolo).</>
                : 'Só letras e números. Guarde esse nome: é ele que aparece nos disparos.'}
          </div>

          </>
          )}

          {/* Status e QR ficam FORA do bloco acima: quem está no limite de
              aparelhos também precisa reconectar o que já tem. */}
          {statusQR && (
            <div style={{
              padding: '10px 14px', borderRadius: '9px', marginTop: '14px',
              fontSize: '12px', fontFamily: 'monospace',
              background: statusQR === 'conectado' ? 'rgba(16,185,129,0.08)'
                : statusQR.startsWith('erro') ? 'rgba(239,68,68,0.08)' : 'rgba(108,71,255,0.08)',
              color: statusQR === 'conectado' ? '#059669'
                : statusQR.startsWith('erro') ? '#dc2626' : '#6c47ff',
              border: '1px solid rgba(108,71,255,0.2)'
            }}>
              {statusQR === 'conectado'
                ? '✅ Aparelho conectado!'
                : statusQR === 'escaneando'
                ? '📱 Escaneie o QR Code com o WhatsApp'
                : '⚠ ' + statusQR.replace('erro:', '')}
            </div>
          )}

          {qr && (
            <div style={{ textAlign: 'center', padding: '20px', marginTop: '14px',
              background: '#f8f8fc', borderRadius: '12px',
              border: '1px solid rgba(108,71,255,0.15)' }}>
              <div style={{ fontSize: '12px', color: '#52526e', marginBottom: '10px' }}>
                {qr.modo === 'reconectar'
                  ? <>Religando <b style={{ fontFamily: 'monospace' }}>{qr.nome}</b> — escaneie com o <b>mesmo número</b> de antes.</>
                  : <>Conectando <b style={{ fontFamily: 'monospace' }}>{qr.nome}</b>.</>}
              </div>
              <img src={qr.base64} alt="QR Code"
                style={{ width: '200px', height: '200px', borderRadius: '10px' }} />
              <div style={{ fontSize: '12px', color: '#52526e', marginTop: '12px' }}>
                Válido por 2 minutos · Atualize se expirar
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info conta */}
      <div style={{ ...card, marginTop: '16px' }}>
        <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: '700',
          fontSize: '15px', marginBottom: '16px' }}>
          {'Sua conta'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          padding: '12px 0', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: '13px' }}>
          <span style={{ color: '#52526e' }}>ID do dispositivo</span>
          <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#6c47ff' }}>{contaUid || '...'}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          padding: '12px 0', fontSize: '13px' }}>
          <span style={{ color: '#52526e' }}>Plano</span>
          <span style={{ fontWeight: '600', color: temPlano ? '#059669' : '#dc2626' }}>{temPlano ? 'Ativo' : 'Sem plano'}</span>
        </div>
      </div>

    </div>
  );
}

const card = {
  background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: '14px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
};
const input = {
  width: '100%', padding: '10px 12px', borderRadius: '9px',
  border: '1.5px solid rgba(0,0,0,0.1)', fontSize: '14px',
  background: '#f8f8fc', outline: 'none', boxSizing: 'border-box',
  fontFamily: 'DM Sans, sans-serif', color: '#0a0a18'
};
